import type { PrimaryFilterId } from '../../filters/types/filters';
import { dimensionSourceToFilterId } from '../../utils/dimension-source-serialization';
import { useChart } from '../Chart/ChartProvider';
import { filterEffectiveMetrics } from '../ChartConfig/utils/metric-isolation';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import {
  isScreenerMetric,
  type MetricValues,
  type ScreenerMetricValues,
} from '../PageConfigProvider/types/metric';
import {
  MultiMetricProvider,
  type MultiMetricProviderResult,
} from './MultiMetricProvider';
import { CompetitiveLandscapeProvider } from './market-conditions/CompetitiveLandscapeProvider';
import { ScreenerDataProvider } from './ScreenerDataProvider';
import type { DataProviderChildren } from './types';

export const TableMultiMetricProvider = ({
  primaryDimension,
  secondaryDimension,
  children,
}: {
  primaryDimension?: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  children: DataProviderChildren<MultiMetricProviderResult>;
}) => {
  const { item } = useChart();
  const defaultMetricOptions = item?.plot?.defaultMetricOptions || [];

  const effectiveScreenerDimension =
    item?.plot?.dimensionSource && item.plot.dimensionSource !== 'primary'
      ? dimensionSourceToFilterId(item.plot.dimensionSource)
      : undefined;

  if (effectiveScreenerDimension) {
    const metrics: MetricValues[] = defaultMetricOptions.length
      ? (defaultMetricOptions as MetricValues[])
      : ['screener_headcount'];

    return (
      <CompetitiveLandscapeProvider
        primaryDimension={effectiveScreenerDimension}
        metrics={metrics}
      >
        {children}
      </CompetitiveLandscapeProvider>
    );
  }

  const filtered = filterEffectiveMetrics(defaultMetricOptions);
  const effectiveMetrics: MetricValues[] = filtered.length
    ? filtered
    : ['headcount'];

  const screenerMetrics = effectiveMetrics.filter(
    isScreenerMetric
  ) as ScreenerMetricValues[];
  const nonScreenerMetrics = effectiveMetrics.filter(
    (m) => !isScreenerMetric(m)
  );

  if (
    screenerMetrics.length > 0 &&
    nonScreenerMetrics.length === 0 &&
    primaryDimension
  ) {
    return (
      <ScreenerDataProvider
        primaryDimension={primaryDimension}
        metrics={screenerMetrics}
      >
        {children}
      </ScreenerDataProvider>
    );
  }

  return (
    <MultiMetricProvider
      primaryDimension={primaryDimension}
      metrics={effectiveMetrics}
      chartType="table"
      secondaryDimension={secondaryDimension}
    >
      {children}
    </MultiMetricProvider>
  );
};
